import React from "react";
import CollapseSection from "../../../collapse/CollapseSection";
import { Divider, Grid2, MenuItem, Stack, Typography } from "@mui/material";
import SelectApiInput from "../../../input/SelectApiInput";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import TextInput from "../../../input/TextInput";
import { numeralCustom } from "../../../../../utils/helpers";
import NumberInput from "../../../input/NumberInput";
import DatetimeInput from "../../../input/DateTimeInput";
import moment from "moment";
import SelectInput from "../../../input/SelectInput";
import { PTTT_NHAXE } from "../../../../../utils/constants";

interface IInfoSection {
  register?: UseFormRegister<FieldValues> | undefined;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  isEdit?: boolean;
  control?: Control<any, any>;
  errors?: FieldErrors<any>;
}

function InfoSection({
  register,
  control,
  watch,
  setValue,
  isEdit,
  errors,
}: IInfoSection) {
  const nhaXe = watch("nha_xe");
  const giaBan = watch("gia_ban_nt");
  const tienHang = watch("tien_hang_nt");
  const tienCk = watch("tien_ck_nt");
  const soLuong = watch("so_luong");

  return (
    <CollapseSection title="Thông tin chung" show>
      <Stack gap={2}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Stack gap={2}>
              <Controller
                name="ma"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      required
                      readOnly={!!isEdit}
                      label="Mã đặt chỗ"
                      labelWidth="30%"
                      placeholder="Nhập mã đặt chỗ"
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.ma?.message as string}
                    />
                  );
                }}
              />

              <Controller
                name="date_created"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <DatetimeInput
                      label="Ngày đặt"
                      labelWidth="30%"
                      name="date_created"
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
              <Controller
                name="trang_thai"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectApiInput
                      label="Trạng thái"
                      labelWidth="30%"
                      withIdApp={false}
                      placeholder="Chọn trạng thái"
                      apiCode="trangthai"
                      selectTitle="Chọn trạng thái"
                      placeholderSearch="Tìm theo tên trạng thái"
                      getOptionLabel={(option) => option.ten_trang_thai}
                      checkEqual={(value, option) =>
                        value?.ma_trang_thai === option?.ma_trang_thai
                      }
                      searchFields={["ten_trang_thai"]}
                      defaultCondition={{ ma_ct: "VEXE" }}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Stack>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Stack gap={2}>
              <Controller
                name="nha_xe"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectApiInput
                      label="Nhà xe"
                      labelWidth="30%"
                      placeholder="Chọn nhà xe"
                      apiCode="dmnhaxe"
                      selectTitle="Chọn nhà xe"
                      placeholderSearch="Tìm theo mã, tên nhà xe"
                      value={value}
                      onChange={(val) => {
                        onChange(val);
                        setValue("xe_khach", null);
                        setValue("hang_ve", null);
                      }}
                      getOptionLabel={(option) => option.ten_dt}
                      checkEqual={(value, option) =>
                        value?.ma_dt === option?.ma_dt
                      }
                      searchFields={["ma_dt", "ten_dt"]}
                    />
                  );
                }}
              />
              <Controller
                name="xe_khach"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectApiInput
                      disabled={!nhaXe}
                      label="Xe khách"
                      labelWidth="30%"
                      placeholder="Chọn xe khách"
                      apiCode="dmxe"
                      selectTitle="Chọn xe khách"
                      placeholderSearch="Tìm theo nhà xe, biển số"
                      getOptionLabel={(option) =>
                        option.ten_nha_xe
                          ? `${option.ten_nha_xe}${
                              option?.bien_so
                                ? ` - Biển số: ${option?.bien_so}`
                                : ""
                            }`
                          : ""
                      }
                      checkEqual={(value, option) => value?._id === option?._id}
                      searchFields={["nha_xe", "bien_so"]}
                      defaultCondition={{ nha_xe: nhaXe?.ma_dt }}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
              <Controller
                name="pt_thanh_toan"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectInput
                      label="PT thanh toán"
                      placeholder="Chọn phương thức thanh toán"
                      labelWidth="30%"
                      options={PTTT_NHAXE}
                      value={value}
                      onChange={onChange}
                    />
                  );
                }}
              />
            </Stack>
          </Grid2>
        </Grid2>
        <Divider />
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Stack gap={2}>
              <Controller
                name="hang_ve"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectApiInput
                      disabled={!nhaXe}
                      label="Hạng vé"
                      labelWidth="30%"
                      placeholder="Chọn hạng vé"
                      apiCode="hangvexe"
                      selectTitle="Chọn hạng vé"
                      placeholderSearch="Tìm theo mã, tên"
                      defaultCondition={{ nhaxe: nhaXe?.ma_dt }}
                      value={value}
                      onChange={(val) => {
                        onChange(val);
                        // set gia_ve mac dinh
                        setValue("gia_ban_nt", val?.gia_ve || 0);
                        setValue("tien_hang_nt", val?.gia_ve || 0);
                        setValue("tien_tt_nt", val?.gia_ve || 0);
                        setValue("tien_ck_nt", 0);
                        setValue("so_luong", 1);
                        // set loai_xe
                        if (val?.loaixe) {
                          setValue("loai_xe", {
                            _id: val?.loaixe,
                            name: val?.ten_loai_xe,
                          });
                        }
                        // set route
                        if (val?.route) {
                          setValue("route", {
                            _id: val?.route,
                            name: val?.ten_tuyen,
                          });
                        }
                      }}
                      getOptionLabel={(option) =>
                        `${option.ma} - ${option.ten}`
                      }
                      renderOption={(option, { key, isEqual, onClick }) => {
                        return (
                          <MenuItem
                            key={key}
                            onClick={onClick}
                            sx={{
                              flexDirection: "column",
                              alignItems: "flex-start",
                              borderRadius: "10px",
                              backgroundColor: isEqual ? "primary.500" : "",
                              "&:hover": {
                                backgroundColor: isEqual ? "primary.500" : "",
                              },
                            }}
                          >
                            <Stack direction="row" alignItems="center" gap={2}>
                              <Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontWeight: 600,
                                    color: "primary.main",
                                  }}
                                >
                                  Tuyến:
                                </Typography>{" "}
                                {option.ten_tuyen}
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" gap={2}>
                              <Typography>
                                <Typography
                                  component="span"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Mã:
                                </Typography>{" "}
                                {option.ma}
                              </Typography>
                              <Typography>
                                <Typography
                                  component="span"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Tên
                                </Typography>{" "}
                                {option.ten}
                              </Typography>
                              <Typography>
                                <Typography
                                  component="span"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Giá vé
                                </Typography>{" "}
                                {numeralCustom(option.gia_ve).format()}
                              </Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" gap={2}>
                              <Typography>
                                <Typography
                                  component="span"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Nhà xe:
                                </Typography>{" "}
                                {option.ten_nhaxe}
                              </Typography>
                              <Typography>
                                <Typography
                                  component="span"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Loại xe:
                                </Typography>{" "}
                                {option.ten_loai_xe}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        );
                      }}
                      checkEqual={(value, option) => value?.ma === option?.ma}
                      searchFields={["ma", "ten"]}
                    />
                  );
                }}
              />
              <Controller
                name="tg_khoi_hanh"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <DatetimeInput
                      label="Ngày đi"
                      labelWidth="30%"
                      name="tg_khoi_hanh"
                      value={value}
                      onChange={(val) => {
                        const hieu_luc_tu = moment(val).format("YYYY-MM-DD");
                        const hieu_luc_den = moment(val)
                          .add(5, "days")
                          .format("YYYY-MM-DD");
                        onChange(val);
                        setValue("hieu_luc_tu", hieu_luc_tu);
                        setValue("hieu_luc_den", hieu_luc_den);
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="loai_xe"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectApiInput
                      label="Loại xe"
                      labelWidth="30%"
                      placeholder="Chọn loại xe"
                      apiCode="dmloaixe"
                      selectTitle="Chọn loại xe"
                      placeholderSearch="Tìm theo mã, tên nhà xe"
                      value={value}
                      onChange={onChange}
                      getOptionLabel={(option) => option.name}
                      checkEqual={(value, option) => value?._id === option?._id}
                      searchFields={["name"]}
                    />
                  );
                }}
              />
              <Controller
                name="route"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectApiInput
                      label="Tuyến đường"
                      labelWidth="30%"
                      placeholder="Chọn tuyến đường"
                      apiCode="route"
                      selectTitle="Chọn tuyến đường"
                      placeholderSearch="Tìm theo tên, điểm đi, điểm đến"
                      value={value}
                      onChange={onChange}
                      getOptionLabel={(option) => option.name}
                      checkEqual={(value, option) => value?._id === option?._id}
                      searchFields={["name", "start", "end"]}
                    />
                  );
                }}
              />
              <TextInput
                label="Ghế"
                labelWidth="30%"
                placeholder="Nhập số ghế"
                name="ma_ghe"
                register={register}
              />
              <TextInput
                label="Tên khách"
                labelWidth="30%"
                placeholder="Nhập tên khách hàng"
                name="ten_kh"
                register={register}
              />
              <TextInput
                label="SĐT khách"
                labelWidth="30%"
                placeholder="Nhập số điện thoại khách hàng"
                name="sdt_kh"
                register={register}
              />
            </Stack>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Stack gap={2}>
              <Controller
                name="gia_ban_nt"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      label="Giá vé"
                      labelWidth="30%"
                      placeholder="Nhập giá vé"
                      value={numeralCustom(value).format()}
                      onChange={(e) => {
                        const val = numeralCustom(e.target.value).value();
                        const tienHang = (val || 0) * (soLuong || 0);
                        setValue("tien_hang_nt", tienHang);
                        setValue("tien_tt_nt", tienHang - (tienCk || 0));
                        onChange(val);
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="so_luong"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <NumberInput
                      label="Số lượng"
                      labelWidth="30%"
                      placeholder="Nhập số lượng đặt"
                      value={value}
                      onChange={(e) => {
                        const val = e.target.value;
                        const tienHang = Number(val || 0) * giaBan;
                        const thanhTien = (tienHang || 0) - (tienCk || 0);
                        onChange(val);
                        setValue("tien_hang_nt", tienHang);
                        setValue("tien_tt_nt", thanhTien);
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="tien_hang_nt"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      label="Tiền vé"
                      labelWidth="30%"
                      placeholder="Nhập tiền vé"
                      value={numeralCustom(value).format()}
                      onChange={(e) => {
                        const val = numeralCustom(e.target.value).value();
                        onChange(val);
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="tien_ck_nt"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      label="Tiền CK"
                      labelWidth="30%"
                      placeholder="Nhập tiền chiết khấu"
                      value={numeralCustom(value).format()}
                      onChange={(e) => {
                        const val = numeralCustom(e.target.value).value();
                        const thanhTien = tienHang - (val || 0);
                        onChange(val);
                        setValue("tien_tt_nt", thanhTien);
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="tien_tt_nt"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TextInput
                      label="Thành tiền"
                      labelWidth="30%"
                      placeholder="Nhập thành tiền"
                      value={numeralCustom(value).format()}
                      onChange={(e) => {
                        const val = numeralCustom(e.target.value).value();
                        onChange(val);
                      }}
                    />
                  );
                }}
              />
              <TextInput
                multiline
                label="Ghi chú"
                labelWidth="30%"
                placeholder="Nhập ghi chú"
                name="dien_giai"
                minRows={3}
                maxRows={5}
                register={register}
              />
            </Stack>
          </Grid2>
        </Grid2>
      </Stack>
    </CollapseSection>
  );
}

export default InfoSection;
